import { useMemo, useRef, useState } from 'react';
import { Search, Check, Undo2, Layers, ImageOff } from 'lucide-react';
import { getAllProducts, getAllCategories, getCategory, normalize } from '../../data/catalog';
import { useQueue } from '../../context/QueueContext';
import {
  VARIANT_STATUS,
  VARIANT_STATUS_LABELS,
  PRODUCT_UNIT,
  getVariantStatus,
  setVariantStatus,
  useAvailabilityTick,
} from '../../utils/availability';

const ORIGIN = 'ATUALIZAR DISPONIBILIDADE';

const QUICK_ACTIONS = [
  { key: 'disponivel', label: 'DISPONÍVEL', status: VARIANT_STATUS.DISPONIVEL },
  { key: 'sem-estoque', label: 'SEM ESTOQUE', status: VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL },
  { key: 'revisar', label: 'REVISAR', status: VARIANT_STATUS.REVISAR },
  { key: 'sem-previsao', label: 'SEM PREVISÃO', status: VARIANT_STATUS.SEM_PREVISAO, adminOnly: true },
];

const STATUS_FILTERS = [
  { key: 'todos', label: 'Todos' },
  { key: VARIANT_STATUS.DISPONIVEL, label: 'Disponíveis' },
  { key: VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL, label: 'Sem estoque' },
  { key: VARIANT_STATUS.REVISAR, label: 'Revisar' },
  { key: VARIANT_STATUS.SEM_PREVISAO, label: 'Sem previsão', adminOnly: true },
];

const STATUS_BADGE_STYLE = {
  DISPONIVEL: 'bg-emerald-100 text-emerald-800',
  TEMPORARIAMENTE_INDISPONIVEL: 'bg-amber-100 text-amber-800',
  REVISAR: 'bg-sky-100 text-sky-800',
  SEM_PREVISAO: 'bg-slate-200 text-slate-700',
};

export default function AdminAvailability() {
  const { user } = useQueue();
  const tick = useAvailabilityTick();
  const isAdmin = user.role === 'admin';

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [catFilter, setCatFilter] = useState('todas');
  const [toast, setToast] = useState(null);
  const [confirmSemPrevisao, setConfirmSemPrevisao] = useState(null);
  const [batchMode, setBatchMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [batchConfirm, setBatchConfirm] = useState(null);
  const toastTimer = useRef(null);

  const allVariants = useMemo(
    () =>
      getAllProducts().flatMap((p) =>
        p.hasVariants === false
          ? // Produto sem variantes: o status pertence ao próprio produto.
            [{ product: p, variant: null, code: `${p.slug}::${PRODUCT_UNIT}`, status: getVariantStatus(p, null) }]
          : p.variants.map((v) => ({
              product: p,
              variant: v,
              code: `${p.slug}::${v.id}`,
              status: getVariantStatus(p, v.id),
            }))
      ),
    // tick: recomputa status imediatamente após qualquer alteração, sem recarregar
    [q, statusFilter, catFilter, tick]
  );

  const results = useMemo(() => {
    const tokens = normalize(q).split(/\s+/).filter(Boolean);
    return allVariants.filter(({ product, variant, code, status }) => {
      if (statusFilter !== 'todos' && status !== statusFilter) return false;
      if (catFilter !== 'todas' && product.categorySlug !== catFilter) return false;
      if (!tokens.length) return true;
      const cat = getCategory(product.categorySlug);
      const hay = normalize(
        [product.name, variant?.name || '', cat?.name, code, ...(product.colorTags || []), ...(product.keywords || [])].join(' ')
      );
      return tokens.every((t) => hay.includes(t));
    });
  }, [allVariants, q, statusFilter, catFilter]);

  const showToast = (undoFn) => {
    clearTimeout(toastTimer.current);
    setToast({ undoFn });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const applyStatus = (entry, newStatus) => {
    const previous = entry.status;
    const res = setVariantStatus({
      product: entry.product,
      variantId: entry.variant?.id || null,
      newStatus,
      user,
      origin: ORIGIN,
    });
    if (!res.ok) return;
    showToast(() =>
      setVariantStatus({ product: entry.product, variantId: entry.variant?.id || null, newStatus: previous, user, origin: ORIGIN })
    );
  };

  const onAction = (entry, action) => {
    if (action.status === entry.status) return;
    if (action.status === VARIANT_STATUS.SEM_PREVISAO) {
      setConfirmSemPrevisao({ entry, action });
      return;
    }
    applyStatus(entry, action.status);
  };

  const toggleSelect = (code) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const runBatch = (action) => {
    setBatchConfirm(null);
    for (const code of selected) {
      const entry = allVariants.find((e) => e.code === code);
      if (entry) {
        setVariantStatus({ product: entry.product, variantId: entry.variant?.id || null, newStatus: action.status, user, origin: `${ORIGIN} (LOTE)` });
      }
    }
    setSelected([]);
    setBatchMode(false);
    showToast(null);
  };

  const batchActions = QUICK_ACTIONS.filter((a) => !a.adminOnly || isAdmin);

  return (
    <div data-testid="availability-page" className={batchMode && selected.length > 0 ? 'pb-44' : 'pb-24'}>
      <h2 className="font-serif text-2xl text-navy">Atualizar Disponibilidade</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Busque, identifique pela foto e altere o status em segundos — sem abrir o cadastro completo.
      </p>

      <form onSubmit={(e) => e.preventDefault()} role="search" className="mt-4">
        <label htmlFor="availability-search" className="sr-only">Buscar tecido, variante ou código</label>
        <div className="flex w-full">
          <input
            id="availability-search"
            data-testid="availability-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar tecido, variante ou código"
            className="h-14 w-full rounded-l-2xl border border-r-0 border-input bg-white px-5 text-base outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
          />
          <span className="flex h-14 items-center rounded-r-2xl border border-l-0 border-input bg-white px-4 text-navy">
            <Search className="h-5 w-5" />
          </span>
        </div>
      </form>

      <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4" data-testid="availability-status-filters">
        {STATUS_FILTERS.filter((f) => !f.adminOnly || isAdmin).map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            data-testid={`availability-filter-status-${String(f.key).toLowerCase()}`}
            aria-pressed={statusFilter === f.key}
            className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
              statusFilter === f.key ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="mt-2 sm:hidden">
        <label htmlFor="availability-category-select" className="sr-only">Filtrar por categoria</label>
        <select
          id="availability-category-select"
          data-testid="availability-category-select"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="h-12 w-full rounded-xl border border-input bg-white px-4 text-sm font-medium outline-none focus:border-navy"
        >
          <option value="todas">Categoria: Todas</option>
          {getAllCategories().map((c) => (
            <option key={c.slug} value={c.slug}>Categoria: {c.name}</option>
          ))}
        </select>
      </div>
      <div className="no-scrollbar mt-2 hidden gap-2 overflow-x-auto sm:flex" data-testid="availability-category-filters">
        <button
          onClick={() => setCatFilter('todas')}
          data-testid="availability-filter-category-todas"
          aria-pressed={catFilter === 'todas'}
          className={`shrink-0 rounded-full border px-4 py-2 text-xs font-medium ${catFilter === 'todas' ? 'border-navy bg-navy text-white' : 'border-navy/15 text-foreground/60'}`}
        >
          Todas as categorias
        </button>
        {getAllCategories().map((c) => (
          <button
            key={c.slug}
            onClick={() => setCatFilter(c.slug)}
            data-testid={`availability-filter-category-${c.slug}`}
            aria-pressed={catFilter === c.slug}
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-medium ${catFilter === c.slug ? 'border-navy bg-navy text-white' : 'border-navy/15 text-foreground/60'}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="availability-result-count">
          {results.length} {results.length === 1 ? 'item' : 'itens'}
        </p>
        <button
          onClick={() => { setBatchMode((b) => !b); setSelected([]); }}
          data-testid="batch-toggle-button"
          aria-pressed={batchMode}
          className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-semibold ${batchMode ? 'border-navy bg-navy text-white' : 'border-navy/20 text-navy hover:bg-navy-light'}`}
        >
          <Layers className="h-4 w-4" /> {batchMode ? 'SAIR DA SELEÇÃO' : 'SELECIONAR VÁRIAS'}
        </button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="availability-results">
        {results.map((entry) => {
          const cat = getCategory(entry.product.categorySlug);
          const isSelected = selected.includes(entry.code);
          const vId = entry.variant?.id || 'produto';
          const vName = entry.variant?.name || null;
          const imgSrc = entry.variant?.image || entry.product.coverImage || entry.product.image || null;
          return (
            <div
              key={entry.code}
              className={`rounded-2xl border bg-white p-3 transition-colors ${isSelected ? 'border-navy ring-2 ring-navy/30' : 'border-navy/10'}`}
              data-testid={`availability-card-${entry.product.slug}-${vId}`}
            >
              <div className="flex gap-3">
                {batchMode && (
                  <button
                    onClick={() => toggleSelect(entry.code)}
                    data-testid={`batch-select-${entry.product.slug}-${vId}`}
                    aria-pressed={isSelected}
                    aria-label={`Selecionar ${entry.product.name}${vName ? ` ${vName}` : ''}`}
                    className={`flex h-7 w-7 shrink-0 items-center justify-center self-start rounded-lg border-2 ${isSelected ? 'border-navy bg-navy text-white' : 'border-navy/25 bg-white'}`}
                  >
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                )}
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={vName ? `${entry.product.name} - ${vName}` : entry.product.name}
                    loading="lazy"
                    className="h-24 w-24 shrink-0 rounded-xl object-cover"
                  />
                ) : (
                  <div
                    className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl bg-navy-light"
                    data-testid={`availability-photo-empty-${entry.product.slug}-${vId}`}
                  >
                    <ImageOff className="h-6 w-6 text-navy/40" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{cat?.name}</p>
                  <p className="truncate font-medium leading-tight">{entry.product.name}</p>
                  {vName ? (
                    <p className="truncate text-sm text-muted-foreground">{vName}</p>
                  ) : (
                    <p className="truncate text-sm font-medium text-navy/70" data-testid={`availability-unit-${entry.product.slug}`}>
                      Produto sem variantes
                    </p>
                  )}
                  <p className="mt-0.5 truncate text-[10px] text-muted-foreground/70">Cód: {entry.variant ? entry.code : entry.product.slug}</p>
                  <span
                    className={`mt-1.5 inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_BADGE_STYLE[entry.status]}`}
                    data-testid={`availability-status-${entry.product.slug}-${vId}`}
                  >
                    {VARIANT_STATUS_LABELS[entry.status]}
                  </span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {QUICK_ACTIONS.filter((a) => !a.adminOnly || isAdmin).map((a) => (
                  <button
                    key={a.key}
                    onClick={() => onAction(entry, a)}
                    data-testid={`availability-action-${entry.product.slug}-${vId}-${a.key}`}
                    disabled={a.status === entry.status}
                    className={`min-h-11 rounded-xl border px-2 py-2.5 text-[11px] font-bold tracking-wide transition-colors ${
                      a.status === entry.status
                        ? 'cursor-default border-navy bg-navy text-white'
                        : a.key === 'sem-previsao'
                          ? 'border-red-300 text-red-700 hover:bg-red-50'
                          : 'border-navy/20 text-navy hover:bg-navy-light'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {results.length === 0 && (
        <p className="mt-8 rounded-2xl border border-dashed border-navy/20 bg-white p-10 text-center text-sm text-muted-foreground" data-testid="availability-empty">
          Nenhum item encontrado para os filtros atuais.
        </p>
      )}

      {toast && (
        <div className="fixed inset-x-0 bottom-4 z-[75] flex justify-center px-4" data-testid="availability-toast">
          <div className="flex items-center gap-4 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white shadow-lg">
            Disponibilidade atualizada.
            {toast.undoFn && (
              <button
                onClick={() => { clearTimeout(toastTimer.current); toast.undoFn(); setToast(null); }}
                data-testid="availability-undo-button"
                className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold hover:bg-white/25"
              >
                <Undo2 className="h-3.5 w-3.5" /> DESFAZER
              </button>
            )}
          </div>
        </div>
      )}

      {batchMode && selected.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-[74] border-t border-navy/10 bg-white px-4 py-3 shadow-[0_-4px_20px_rgba(15,41,66,0.12)]" data-testid="batch-bar">
          <p className="text-center text-xs font-semibold text-navy">{selected.length} selecionadas</p>
          <div className="mx-auto mt-2 flex max-w-lg flex-wrap justify-center gap-2">
            {batchActions.map((a) => (
              <button
                key={a.key}
                onClick={() => setBatchConfirm(a)}
                data-testid={`batch-action-${a.key}`}
                className={`rounded-full px-4 py-2.5 text-[11px] font-bold ${a.key === 'sem-previsao' ? 'bg-red-600 text-white' : 'bg-navy text-white'}`}
              >
                {a.key === 'disponivel' && 'MARCAR COMO DISPONÍVEL'}
                {a.key === 'sem-estoque' && 'MARCAR COMO SEM ESTOQUE'}
                {a.key === 'revisar' && 'MARCAR PARA REVISÃO'}
                {a.key === 'sem-previsao' && 'SEM PREVISÃO'}
              </button>
            ))}
          </div>
        </div>
      )}

      {(confirmSemPrevisao || batchConfirm) && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-modal="true" data-testid="availability-confirm-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {confirmSemPrevisao ? (
              <>
                <p className="font-serif text-lg text-navy">Confirmar “Sem previsão de reposição”</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  <strong>{confirmSemPrevisao.entry.product.name}{confirmSemPrevisao.entry.variant ? ` — ${confirmSemPrevisao.entry.variant.name}` : ''}</strong>{' '}
                  será retirado do catálogo público e ficará arquivado. O cadastro e o histórico serão preservados.
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button onClick={() => setConfirmSemPrevisao(null)} data-testid="confirm-cancel-button" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">
                    CANCELAR
                  </button>
                  <button
                    onClick={() => { applyStatus(confirmSemPrevisao.entry, VARIANT_STATUS.SEM_PREVISAO); setConfirmSemPrevisao(null); }}
                    data-testid="confirm-sem-previsao-button"
                    className="rounded-full bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    CONFIRMAR
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="font-serif text-lg text-navy">Confirmar alteração em lote</p>
                <p className="mt-2 text-sm text-muted-foreground" data-testid="batch-confirm-text">
                  Alterar disponibilidade de {selected.length} {selected.length === 1 ? 'variante' : 'variantes'} para{' '}
                  <strong>{VARIANT_STATUS_LABELS[batchConfirm.status]}</strong>?
                </p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button onClick={() => setBatchConfirm(null)} data-testid="batch-cancel-button" className="rounded-full border border-navy/20 py-3 text-sm font-semibold text-navy hover:bg-navy-light">
                    CANCELAR
                  </button>
                  <button onClick={() => runBatch(batchConfirm)} data-testid="batch-confirm-button" className="rounded-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navy-hover">
                    CONFIRMAR
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
